import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CurrencyInput from 'react-currency-input-field';
import { IDateInput, IInput, IAmountInput } from './Input.types';
import { ReactComponent as EyeClose } from '../../assets/images/eye-close.svg';
import { ReactComponent as EyeOpen } from '../../assets/images/eye-open.svg';
import { ReactComponent as InputErrorIcon } from '../../assets/images/input-error-icon.svg';
import { ReactComponent as DateInputIcon } from '../../assets/images/calendar-icon.svg';
import Loading from '../Loading';

import InputStyles from './Input.module.scss';
import DateInputStyles from './DateInput.module.scss';
import './AmountInput.scss';

const Input = ({
  error,
  icon,
  label,
  name,
  onChange,
  onFocus,
  onBlur,
  placeholder,
  type,
  value,
  disabled,
  title,
  required,
  inputClass,
  containerClass,
  pattern,
  hideErrorIcon,
  loading,
  ...props
}: IInput) => {
  const [showPassword, setShowPassword] = useState(false);

  const onPasswordClick = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        position: 'relative'
      }}>
      {label && <div className={InputStyles.input__label}>{label}</div>}
      <div
        className={
          !error
            ? `${InputStyles.input} ${containerClass}`
            : `${InputStyles.input} ${containerClass} ${InputStyles.errored}`
        }
        style={{
          backgroundColor: disabled ? '#f6f6f6' : '#fff'
        }}>
        <div className={InputStyles.input_content}>
          {icon && <div className={InputStyles.input_content_icon}>{icon}</div>}
          <input
            type={showPassword ? 'text' : type}
            required={required}
            name={name}
            title={title}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            className={`${InputStyles.input_content_field} ${inputClass}`}
            pattern={pattern}
            {...props}
          />
          {type === 'password' && (
            <button
              type="button"
              onClick={onPasswordClick}
              className={InputStyles.input_content_passicon}>
              {showPassword ? <EyeClose /> : <EyeOpen />}
            </button>
          )}
          {loading && (
            <div className={InputStyles.input_loading}>
              <Loading />
            </div>
          )}
          {error && type !== 'password' && !hideErrorIcon && (
            <div className={InputStyles.input_content_erroricon}>
              <InputErrorIcon />
            </div>
          )}
        </div>
      </div>
      <div className={InputStyles.error}>{error && error}</div>
    </div>
  );
};

export default Input;

export const DateInput = ({
  onChange,
  label,
  className,
  error,
  maxDate,
  disabled,
  value,
  name,
  placeholder = 'DD/MM/YYYY',
  dateFormat = 'DD/MM/YYYY'
}: IDateInput) => {
  const [startDate, setStartDate] = useState<Date | null>(null);

  const handleFormChange = (date: any) => {
    setStartDate(date);
    // eslint-disable-next-line no-unused-expressions
    onChange && onChange(date);
  };

  return (
    <div>
      {label && <p className={DateInputStyles.datepicker_content_label}>{label}</p>}
      <div
        className={
          !error
            ? `${DateInputStyles.datepicker} ${className} app-date-picker ${
                disabled ? DateInputStyles.disabled : DateInputStyles.default
              }`
            : `${DateInputStyles.datepicker} ${className} ${DateInputStyles.errored} app-date-picker`
        }>
        <div className={DateInputStyles.datepicker_content}>
          <DatePicker
            name={name}
            className={DateInputStyles.datepicker_content_item}
            selected={startDate}
            onChange={handleFormChange}
            maxDate={maxDate}
            dateFormat={dateFormat}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            placeholderText={placeholder}
            disabled={disabled}
            value={value}
            autoComplete="off"
            onKeyDown={(e: any) => {
              e.preventDefault();
            }}
          />
          <DateInputIcon className={DateInputStyles.datepicker_content_icon} />
        </div>

        <div className={DateInputStyles.datepicker_error}>{error && error}</div>
      </div>
    </div>
  );
};

export const AmountInput = ({
  name,
  defaultValue,
  className,
  placeholder,
  label,
  value,
  onChange,
  error,
  disabled,
  icon,
  labelColor,
  iconMarginRight,
  ...props
}: IAmountInput) => {
  return (
    <div>
      {label && (
        <div className="amount-input_customLabel" style={{ color: labelColor }}>
          {label}
        </div>
      )}
      <div
        className={`amount-input_field ${error && 'amount-input_errored'} ${
          disabled ? 'amount-input_disabled' : ''
        }`}>
        {icon && (
          <div className="amount-input-icon" style={{ marginRight: iconMarginRight }}>
            {icon}
          </div>
        )}
        <CurrencyInput
          className={`amount-input_customInput ${className}`}
          name={name}
          placeholder={placeholder}
          value={value}
          defaultValue={defaultValue}
          decimalsLimit={2}
          onValueChange={onChange}
          prefix="C$"
          disabled={disabled}
          autoComplete="off"
          allowNegativeValue={false}
          {...props}
        />
        <div className="amount-input_error">{error && error}</div>
      </div>
    </div>
  );
};
